import type { Transaction, CategorizationResult, CategorizationContext } from '../types';
import Fuse from 'fuse.js';
import {
  PRIORITY_RULES,
  EXACT_MATCH_RULES,
  INCOME_RULES,
  P2P_RULES,
  STANDARD_RULES,
  DISPLAY_NAME_MAPPINGS,
  type CategorizationRule,
  type KeywordMapping,
} from './rules';

// Confidence thresholds
const CONFIDENCE_EXACT_MATCH = 0.98;
const CONFIDENCE_START_WORD_BOUNDARY = 0.95;
const CONFIDENCE_START_NO_BOUNDARY = 0.93;
const CONFIDENCE_WORD_BOUNDARY = 0.92;
const CONFIDENCE_BASE_SUBSTRING = 0.70;
const CONFIDENCE_RATIO_MULTIPLIER = 0.28;
const CONFIDENCE_MULTI_KEYWORD_BOOST = 0.05;
const CONFIDENCE_MULTI_KEYWORD_FALLBACK = 0.85;
const CONFIDENCE_WORD_BOUNDARY_FALLBACK = 0.95;
const CONFIDENCE_PRIORITY = 0.95;
const CONFIDENCE_INCOME_P2P = 0.90;
const CONFIDENCE_SPA_SPECIAL = 0.85;
const CONFIDENCE_FUZZY_HIGH_THRESHOLD = 0.65;
const CONFIDENCE_FUZZY_LOW_THRESHOLD = 0.40;
const CONFIDENCE_NONE = 0.30;

/**
 * Hybrid Categorizer State
 *
 * Strategy:
 * 1. Use keyword .includes() matching (works great for merchant variations)
 * 2. If uncategorized, try fuzzy matching as fallback (catches typos/edge cases)
 * 3. Provide nuanced confidence scores based on match quality
 */
export type HybridCategorizerState = {
  name: string;
  priority: number;
  fuseInstance: Fuse<{ category: string; merchant: string }>;
  regexCache: Map<string, RegExp>;
  sortedDisplayMappings: KeywordMapping[];
};

/**
 * Create a new HybridCategorizer state
 */
export function createHybridCategorizer(): HybridCategorizerState {
  // Build fuzzy search for fallback
  const merchantList = STANDARD_RULES.flatMap((rule) =>
    rule.keywords.map((keyword) => ({
      category: rule.category,
      merchant: keyword,
    }))
  );

  const fuseInstance = new Fuse(merchantList, {
    keys: ['merchant'],
    threshold: 0.5, // Slightly more lenient (was 0.4) - allows more fuzzy matches
    distance: 100,
    minMatchCharLength: 3,
    includeScore: true,
    ignoreLocation: true,
  });

  // Pre-compile all keyword regexes for word boundary matching
  const regexCache = new Map<string, RegExp>();
  const allRules = [
    ...PRIORITY_RULES,
    ...EXACT_MATCH_RULES,
    ...INCOME_RULES,
    ...P2P_RULES,
    ...STANDARD_RULES,
  ];

  for (const rule of allRules) {
    for (const keyword of rule.keywords) {
      if (!regexCache.has(keyword)) {
        const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        regexCache.set(keyword, new RegExp(`\\b${escapedKeyword}\\b`));
      }
    }
  }

  // Pre-sort display name mappings (longest first) to avoid re-sorting on every transaction
  const sortedDisplayMappings = [...DISPLAY_NAME_MAPPINGS].sort(
    (a, b) => b.keyword.length - a.keyword.length
  );

  return {
    name: "HybridCategorizer",
    priority: 30,
    fuseInstance,
    regexCache,
    sortedDisplayMappings,
  };
}

/**
 * Normalize description for consistent matching
 * - Lowercase for case-insensitive comparison
 * - Remove apostrophes (don't -> dont)
 * - Replace punctuation with spaces
 * - Remove long numbers (7+ digits - phone/account numbers)
 * - Collapse multiple spaces
 */
function normalizeDescription(description: string): string {
  return description
    .toLowerCase()
    .replace(/['']/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\b\d{7,}\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Categorize a transaction using hybrid matching
 */
export async function categorizeWithHybrid(
  state: HybridCategorizerState,
  transaction: Transaction,
  _context: CategorizationContext = {}
): Promise<CategorizationResult> {
  const desc = normalizeDescription(transaction.description);
  const type = transaction.type;

  // Step 1: Check Priority Rules (exact - transfers/payments)
  const priorityMatch = findKeywordMatch(PRIORITY_RULES, desc, type, state.regexCache);
  if (priorityMatch) {
    return {
      category: priorityMatch.category,
      confidence: CONFIDENCE_PRIORITY,
      method: "hybrid-priority",
    };
  }

  // Step 2: Check Exact Match Rules
  const exactMatch = findKeywordMatch(EXACT_MATCH_RULES, desc, type, state.regexCache);
  if (exactMatch) {
    return {
      category: exactMatch.category,
      confidence: CONFIDENCE_PRIORITY,
      method: "hybrid-exact",
    };
  }

  // Step 3: Check Income Rules
  const incomeMatch = findKeywordMatch(INCOME_RULES, desc, type, state.regexCache);
  if (incomeMatch) {
    return {
      category: incomeMatch.category,
      confidence: CONFIDENCE_INCOME_P2P,
      method: "hybrid-income",
    };
  }

  // Step 4: Check P2P Payment Rules
  const p2pMatch = findKeywordMatch(P2P_RULES, desc, type, state.regexCache);
  if (p2pMatch) {
    return {
      category: p2pMatch.category,
      confidence: CONFIDENCE_INCOME_P2P,
      method: "hybrid-p2p",
    };
  }

  // Step 5: Check Standard Category Rules (keyword .includes())
  const standardMatch = findKeywordMatch(STANDARD_RULES, desc, type, state.regexCache);
  if (standardMatch) {
    // Use Fuse to calculate dynamic confidence based on match quality
    const confidence = calculateMatchConfidence(desc, standardMatch);
    const displayName = findDisplayName(desc, state.sortedDisplayMappings);

    return {
      category: standardMatch.category,
      confidence,
      method: "hybrid-keyword",
      displayName: displayName || undefined,
    };
  }

  // Step 6: Special case for 'spa' at start
  if (desc.startsWith("spa")) {
    return {
      category: "Personal Care",
      confidence: CONFIDENCE_SPA_SPECIAL,
      method: "hybrid-special",
    };
  }

  // Step 7: Fuzzy matching fallback for uncategorized
  const fuzzyResult = findFuzzyMatch(state.fuseInstance, desc);
  if (fuzzyResult && fuzzyResult.confidence >= CONFIDENCE_FUZZY_HIGH_THRESHOLD) {
    // High confidence fuzzy match - use the suggested category
    return {
      category: fuzzyResult.category,
      confidence: fuzzyResult.confidence,
      method: "hybrid-fuzzy-fallback",
    };
  }

  // No keyword match - return Uncategorized with actual fuzzy confidence if available
  // This shows how close the transaction was to matching (e.g., 0.55 vs 0.20)
  if (fuzzyResult && fuzzyResult.confidence >= CONFIDENCE_FUZZY_LOW_THRESHOLD) {
    // Fuzzy match exists but confidence is 0.4-0.65 (low-medium)
    // Show actual confidence instead of defaulting to 0.5
    return {
      category: "Uncategorized",
      confidence: fuzzyResult.confidence,
      method: "hybrid-fuzzy-low",
    };
  }

  // No match at all or very poor fuzzy match (< 0.4)
  return {
    category: "Uncategorized",
    confidence: CONFIDENCE_NONE,
    method: "hybrid-none",
  };
}

type KeywordMatchResult = {
  category: string;
  matchCount?: number;
  exactMatch?: boolean;
  wordBoundaryMatch?: boolean;
  matchedKeyword?: string;
} | null;

function findKeywordMatch(
  rules: CategorizationRule[],
  desc: string,
  type: string,
  regexCache: Map<string, RegExp>
): KeywordMatchResult {
  for (const rule of rules) {
    if (rule.types && !rule.types.includes(type)) continue;

    // Handle requiresMultiple
    if (rule.requiresMultiple && rule.requiresMultiple.length > 0) {
      const allGroupsMatch = rule.requiresMultiple.every((group) =>
        group.some((keyword) => desc.includes(keyword))
      );
      if (allGroupsMatch) {
        return { category: rule.category, matchCount: rule.requiresMultiple.length };
      }
    }

    // Handle regular keywords
    if (rule.keywords.length > 0) {
      let matchCount = 0;
      let hasExactMatch = false;
      let hasWordBoundaryMatch = false;
      let firstMatchedKeyword: string | undefined;

      for (const keyword of rule.keywords) {
        if (rule.exact) {
          if (desc.trim() === keyword) {
            hasExactMatch = true;
            matchCount++;
            firstMatchedKeyword = firstMatchedKeyword || keyword;
            break;
          }
        } else {
          if (desc.includes(keyword)) {
            matchCount++;
            firstMatchedKeyword = firstMatchedKeyword || keyword; // Store first matched keyword
            // Check if it's an exact match (entire description)
            if (desc === keyword) {
              hasExactMatch = true;
            }
            // Check if keyword appears as a distinct word (word boundary match)
            // This catches cases like "netflix 1 ca" where "netflix" is clearly the merchant
            const wordBoundaryRegex = regexCache.get(keyword);
            if (wordBoundaryRegex && wordBoundaryRegex.test(desc)) {
              hasWordBoundaryMatch = true;
            }
          }
        }
      }

      if (matchCount > 0) {
        return {
          category: rule.category,
          matchCount,
          exactMatch: hasExactMatch,
          wordBoundaryMatch: hasWordBoundaryMatch,
          matchedKeyword: firstMatchedKeyword,
        };
      }
    }

    // Type-only match
    if (rule.types && rule.keywords.length === 0 && !rule.requiresMultiple) {
      return { category: rule.category };
    }
  }
  return null;
}

function calculateMatchConfidence(
  desc: string,
  match: NonNullable<KeywordMatchResult>
): number {
  // If we have the matched keyword, calculate dynamic confidence based on match quality
  if (match.matchedKeyword) {
    const keyword = match.matchedKeyword;

    // Apply boosts for match quality
    if (match.exactMatch) {
      return CONFIDENCE_EXACT_MATCH;
    }

    // Check if keyword is at the start - very strong signal even without word boundary
    // This handles cases like "caputos" (caputo + s) or "aldis"
    if (desc.startsWith(keyword)) {
      if (match.wordBoundaryMatch) {
        // Perfect: starts with keyword AND has word boundary (e.g., "aldi 40053")
        return CONFIDENCE_START_WORD_BOUNDARY;
      } else {
        // Good: starts with keyword but continues (e.g., "caputos fresh markets")
        // Still very confident the keyword represents the merchant
        return CONFIDENCE_START_NO_BOUNDARY;
      }
    }

    // Word boundary match but not at start (e.g., "tst starbucks")
    if (match.wordBoundaryMatch) {
      return CONFIDENCE_WORD_BOUNDARY;
    }

    // For substring matches without word boundary or start position, use ratio-based confidence
    // This handles cases like "netflix.com" or "netflix 123"
    const keywordLength = keyword.length;
    const descLength = desc.length;
    const ratio = keywordLength / descLength;

    // Base confidence starts at CONFIDENCE_BASE_SUBSTRING and scales up based on ratio
    // ratio=1.0 (exact match) -> 0.98
    // ratio=0.5 (keyword is 50% of desc) -> 0.84
    // ratio=0.2 (keyword is 20% of desc) -> 0.75
    // ratio=0.1 (keyword is 10% of desc) -> 0.73
    let baseConfidence = CONFIDENCE_BASE_SUBSTRING +
                        (ratio * CONFIDENCE_RATIO_MULTIPLIER);

    if (match.matchCount && match.matchCount > 1) {
      // Multiple keywords matched - boost confidence
      baseConfidence = Math.min(
        CONFIDENCE_EXACT_MATCH,
        baseConfidence + CONFIDENCE_MULTI_KEYWORD_BOOST
      );
    }

    return Math.min(
      CONFIDENCE_EXACT_MATCH,
      Math.max(CONFIDENCE_BASE_SUBSTRING, baseConfidence)
    );
  }

  // Fallback to heuristic-based confidence if no keyword info
  let confidence = 0.75; // Base confidence for keyword substring match

  if (match.matchCount && match.matchCount > 1) {
    confidence = CONFIDENCE_MULTI_KEYWORD_FALLBACK;
  }
  if (match.wordBoundaryMatch) {
    confidence = CONFIDENCE_WORD_BOUNDARY_FALLBACK;
  }
  if (match.exactMatch) {
    confidence = CONFIDENCE_EXACT_MATCH;
  }

  return confidence;
}

function findFuzzyMatch(
  fuseInstance: Fuse<{ category: string; merchant: string }>,
  desc: string
): { category: string; confidence: number } | null {
  const results = fuseInstance.search(desc);
  if (results.length === 0) return null;

  const bestMatch = results[0];
  const score = bestMatch.score ?? 1;
  const confidence = 1 - score;

  return {
    category: bestMatch.item.category,
    confidence,
  };
}

function findDisplayName(desc: string, sortedDisplayMappings: KeywordMapping[]): string | null {
  // Use pre-sorted mappings (sorted by keyword length, longest first)
  // This prevents "h m" from matching before "h&m" or longer patterns
  for (const mapping of sortedDisplayMappings) {
    if (desc.includes(mapping.keyword)) {
      return mapping.displayName;
    }
  }
  return null;
}

/**
 * Get categorizer info
 */
export function getHybridCategorizerInfo(state: HybridCategorizerState) {
  return {
    name: state.name,
    priority: state.priority,
    type: "hybrid-matching",
    description: "Uses keyword .includes() matching with fuzzy fallback for edge cases",
  };
}

/**
 * Check if categorizer can handle transaction (always returns true)
 */
export function canCategorizeHybrid(
  _state: HybridCategorizerState,
  _transaction: Transaction,
  _context: CategorizationContext = {}
): boolean {
  return true;
}
